/**
 * 🔒 The Work Order's Labour tile (decision 1908; T1201): every time entry on
 * the job, one row each, with each worker's typed hours drawn as their own row
 * beside them, totalled per line against its budget and for the job as a
 * whole. 🔨 The cost columns leave the server only for a viewer allowed to see
 * the worker's cost rate (decision 0128 applied to the line); for anybody else
 * the fields are absent, never blanked, and the screen reads Withheld.
 */

/** The three ways in under plain names, and the payroll import beside them (decision 1904). */
export const TIME_SOURCE_LABELS: Readonly<Record<string, string>> = {
  JobClock: 'Clock',
  Manual: 'Typed',
  GateSignIn: 'Gate',
  AdpImport: 'Payroll import',
};

export function timeSourceLabel(captureSource: string): string {
  return TIME_SOURCE_LABELS[captureSource] ?? captureSource;
}

/** The invoice that billed an entry (decision 1906). */
export interface LabourInvoiceRef {
  invoiceId: string;
  invoiceNumber: string | null;
  status: string;
}

/** One time entry on the job. `costRate` and `cost` are absent for a viewer the cost rule refuses. */
export interface LabourEntryRow {
  kind: 'entry';
  id: string;
  employmentId: string;
  personId: string;
  personName: string;
  /** The line it names, or null for a Travel entry or a gate entry naming the job alone. */
  lineId: string | null;
  lineLabel: string | null;
  entryType: string | null;
  workDate: string;
  startedAt: string | null;
  endedAt: string | null;
  hours: number;
  captureSource: string;
  sourceLabel: string;
  approvalStatus: string;
  note: string | null;
  /** 🔒 Decision 1906: the entry this one corrects, and why; a correction may carry negative hours. */
  correctsTimeEntryId: string | null;
  correctionReason: string | null;
  /** Whether this entry counts toward its line under decision 1903; a Travel or unlined entry counts toward no line. */
  counts: boolean;
  /** The rates and the cost in whole cents (T2192 item 12); `costRateCents` and `costCents` are absent for a viewer the cost rule refuses. */
  billingRateCents: number | null;
  costRateCents?: number | null;
  costCents?: number | null;
  costWithheld: boolean;
  invoice: LabourInvoiceRef | null;
  /** 🔒 Decision 1906: a billed entry is locked, and only a correction changes what it says. */
  locked: boolean;
}

/** A worker's typed hours on a line, drawn as their own row (decision 1908). */
export interface LabourTypedRow {
  kind: 'typed';
  id: string;
  employmentId: string;
  personId: string;
  personName: string;
  lineId: string;
  lineLabel: string;
  hours: number;
  /** True where the typed figure is what bills, false where the worker's entries count instead (decision 1903). */
  counts: boolean;
  billingRateCents: number;
  costRateCents?: number | null;
  costCents?: number | null;
  costWithheld: boolean;
}

export type LabourRow = LabourEntryRow | LabourTypedRow;

/** One line's totals beside its budget, with the typed figure beside the entries so the trial can be read. */
export interface LabourLineTotal {
  lineId: string;
  lineLabel: string;
  budgetedHours: number | null;
  /** The line's Actual under decision 1903. */
  actualHours: number;
  typedHours: number;
  entryHours: number;
  /** Hours on entries an invoice line names. */
  billedHours: number;
  remainingHours: number | null;
  /** The workers on the line, whom Record time offers. */
  workers: { employmentId: string; name: string }[];
}

export interface WorkOrderLabourAnswer {
  workOrderId: string;
  currencyCode: string;
  rows: LabourRow[];
  lines: LabourLineTotal[];
  job: {
    actualHours: number;
    typedHours: number;
    entryHours: number;
    billedHours: number;
    budgetedHours: number | null;
    remainingHours: number | null;
    /** Whole cents; absent unless the viewer sees every row's cost. */
    costCents?: number;
  };
  /** 🔒 Decision 1904: Record time stands on `workorder.time.typed`. */
  canRecord: boolean;
  /** A desk with the Work Order write records another worker's time; anybody else records their own alone. */
  canRecordForOthers: boolean;
  /** 🔒 Decision 1906: Raise a Correction stands under the Work Order write. */
  canCorrect: boolean;
  viewerEmploymentId: string | null;
}

/** 🔒 Decision 1904: Record time's own refusals, before anything is written. Null where the entry stands. */
export function recordTimeProblem(input: { hours: number | null; note: string | null | undefined; onLine: boolean }): string | null {
  if (!input.note || !input.note.trim()) return 'Record time needs a note saying what the time was';
  if (input.hours === null || !Number.isFinite(input.hours) || input.hours <= 0) return 'Record time needs the hours, or a from and a to, above zero';
  if (input.hours > 24) return 'one entry holds at most one day’s hours';
  if (!input.onLine) return 'the worker is not on this line; assign them to it first';
  return null;
}

/** 🔒 Decision 1906: a correction names the entry it corrects, carries a reason, and moves the hours by something. */
export function correctionProblem(input: { hours: number | null; reason: string | null | undefined; isCorrection: boolean }): string | null {
  if (input.isCorrection) return 'a correction is corrected by correcting the entry it names';
  if (!input.reason || !input.reason.trim()) return 'a correction carries the reason for it';
  if (input.hours === null || !Number.isFinite(input.hours) || input.hours === 0) return 'a correction moves the hours by something, up or down';
  return null;
}
